/**
 * Central registry of per-input AnalyserNode taps used to drive the
 * InputMeter UI. Runs on any platform that uses the Web Audio graph
 * — which is every platform we support, because Phase 3's native
 * AudioWorklet bridge feeds its output into the Web Audio graph
 * exactly like `MediaStreamAudioSourceNode` does on web. So one tap
 * strategy (AnalyserNode + time-domain peak) works everywhere.
 *
 * Meter IDs:
 *   - `MONITOR_METER_ID` — the legacy single-device Monitor path
 *     owned by `AudioInput.ts`.
 *   - Any `InputSource.id` — per-source tap owned by
 *     `MultiInputRouter`.
 *
 * Readers call `getPeakDbFS(id)` from a RAF loop; the registry
 * owns the buffer allocation so callers don't each malloc their
 * own Float32Array every frame.
 */

/** Sentinel meter id for the legacy Monitor path. */
export const MONITOR_METER_ID = '__monitor__'

interface MeterEntry {
  analyser: AnalyserNode
  /** Re-used Float32 scratch buffer so we don't allocate each
   *  frame when the UI polls the peak. Sized to `fftSize`. */
  buf: Float32Array
}

const registry = new Map<string, MeterEntry>()

/** Create (or return existing) analyser + scratch buffer for the
 *  given id. Caller is responsible for connecting the analyser into
 *  the signal chain (tap it as a pass-through before the bus sink).
 *  The analyser is returned so the audio graph code can hook it up
 *  to whatever source node it owns. */
export function ensureMeter(ctx: AudioContext, id: string): AnalyserNode {
  const existing = registry.get(id)
  if (existing) return existing.analyser
  const analyser = ctx.createAnalyser()
  // 512-sample window ≈ 10.7 ms at 48 kHz — small enough that peak
  // readings reflect current loudness without lag; large enough
  // that a single-frame dropout doesn't ping-pong the meter to 0.
  analyser.fftSize = 512
  analyser.smoothingTimeConstant = 0
  const buf = new Float32Array(analyser.fftSize)
  registry.set(id, { analyser, buf })
  return analyser
}

/** Drop the meter entry + disconnect its analyser. Call when a
 *  source is removed or the legacy Monitor flips off. Silently
 *  tolerates unknown ids so UI code can call on unmount without
 *  tracking life cycle explicitly. */
export function disposeMeter(id: string): void {
  const entry = registry.get(id)
  if (!entry) return
  try {
    entry.analyser.disconnect()
  } catch {
    /* already disconnected */
  }
  registry.delete(id)
}

/** Peak amplitude over the analyser's current sample window, in
 *  dBFS. Returns `-120` when the meter doesn't exist or the window
 *  is silent (avoids log10(0) = -Infinity). */
export function getPeakDbFS(id: string): number {
  const entry = registry.get(id)
  if (!entry) return -120
  entry.analyser.getFloatTimeDomainData(entry.buf)
  let peak = 0
  const n = entry.buf.length
  for (let i = 0; i < n; i++) {
    const abs = Math.abs(entry.buf[i] ?? 0)
    if (abs > peak) peak = abs
  }
  if (peak <= 0) return -120
  // Clamp the floor so the meter has a usable low end without
  // decay tails running into -Infinity on truly silent input.
  return Math.max(-120, 20 * Math.log10(peak))
}

/**
 * Per-channel FFT tap registry. Parallel to the peak-meter registry
 * above but targeted at the SPAN-style spectrum view: two
 * AnalyserNodes (L + R) per analyzer instance, returning
 * frequency-domain data in dB.
 *
 * Keyed by a caller-supplied analyzer id (the FFT analyzer plugin
 * generates one per instance). Consumers read with
 * `getFftSnapshot(id)` from a RAF loop — scratch buffers are
 * re-used so there's no per-frame allocation.
 */
interface FftMeterEntry {
  left: AnalyserNode
  right: AnalyserNode
  leftBuf: Float32Array
  rightBuf: Float32Array
  sampleRate: number
}

const fftRegistry = new Map<string, FftMeterEntry>()

/**
 * Create (or return existing) per-channel AnalyserNodes + scratch
 * buffers for the given analyzer id. Caller wires the analysers into
 * the audio graph (typically on the output of a ChannelSplitter).
 *
 * `fftSize` must be a power of two between 32 and 32768 (the Web
 * Audio spec). The FFT analyzer plugin exposes 512 / 1024 / 2048 /
 * 4096 / 8192 as select options.
 */
export function ensureFftMeter(
  ctx: AudioContext,
  id: string,
  fftSize: number = 2048,
): { left: AnalyserNode; right: AnalyserNode } {
  const existing = fftRegistry.get(id)
  if (existing) {
    return { left: existing.left, right: existing.right }
  }
  const left = ctx.createAnalyser()
  const right = ctx.createAnalyser()
  left.fftSize = fftSize
  right.fftSize = fftSize
  // 0 smoothing at the node level — the plugin applies its own
  // time-constant via Tone.FFT's smoothing param, so doubling up
  // would make the display feel sluggish. Consumers that want
  // extra smoothing can EMA the returned frames themselves.
  left.smoothingTimeConstant = 0
  right.smoothingTimeConstant = 0
  const binCount = left.frequencyBinCount
  fftRegistry.set(id, {
    left,
    right,
    leftBuf: new Float32Array(binCount),
    rightBuf: new Float32Array(binCount),
    sampleRate: ctx.sampleRate,
  })
  return { left, right }
}

/**
 * Resize an existing FFT meter's window. Realloc-safe — scratch
 * buffers are replaced in place so RAF consumers see the new size
 * from the next frame. Silently returns if the id isn't registered.
 */
export function resizeFftMeter(id: string, fftSize: number): void {
  const entry = fftRegistry.get(id)
  if (!entry) return
  entry.left.fftSize = fftSize
  entry.right.fftSize = fftSize
  const binCount = entry.left.frequencyBinCount
  entry.leftBuf = new Float32Array(binCount)
  entry.rightBuf = new Float32Array(binCount)
}

/**
 * Read the current frequency-domain snapshot for an analyzer id.
 * Returns dB values directly from `getFloatFrequencyData()` (spec:
 * `20 * log10(magnitude / N)` with dB floor at `minDecibels`).
 *
 * Returned arrays are the registry's internal buffers — callers
 * must not retain them across RAF ticks, but reading + copying is
 * fine. Returns `null` when the id isn't registered so UI code can
 * render a placeholder without crashing.
 */
export function getFftSnapshot(id: string): {
  left: Float32Array
  right: Float32Array
  sampleRate: number
  binCount: number
} | null {
  const entry = fftRegistry.get(id)
  if (!entry) return null
  entry.left.getFloatFrequencyData(entry.leftBuf)
  entry.right.getFloatFrequencyData(entry.rightBuf)
  return {
    left: entry.leftBuf,
    right: entry.rightBuf,
    sampleRate: entry.sampleRate,
    binCount: entry.left.frequencyBinCount,
  }
}

/** Tear-down counterpart to `ensureFftMeter`. Disconnects the
 *  analysers + drops the registry entry. Silent when id is unknown
 *  so callers can fire-and-forget on dispose. */
export function disposeFftMeter(id: string): void {
  const entry = fftRegistry.get(id)
  if (!entry) return
  try {
    entry.left.disconnect()
    entry.right.disconnect()
  } catch {
    /* already disconnected */
  }
  fftRegistry.delete(id)
}
